// Generic Map Constants that can be used across different map providers

import { BadgeStatusType } from "../common/Constants";

export const MAX_LAT = 85.05112878;
export const WORLD_BOUNDS: [[number, number], [number, number]] = [
  [-MAX_LAT, -180],
  [MAX_LAT, 180],
];

export const MAPLIBRE_Z_INDEX = {
  MARKER: 100,
  CONTROL: 200,
  POPUP: 300,
} as const;

// Azure Map Constants
export const AZURE_MAP_API_VERSION = "2.1";
export type mapTileType = "BASE" | "WEATHER";

//Define the base URL and the endpoints
const mapTileEndPoint = process.env.REACT_APP_API_AZURE_MAP_TILES;
const mapSasEndPoint = process.env.REACT_APP_API_AZURE_MAP_SAS;

export const mapApiUrl = `${mapTileEndPoint}`;
export const mapSasApiUrl = `${mapSasEndPoint}`;

// How long (ms) before the sig's `expires` timestamp we should proactively fetch a new one.
export const SAS_REFRESH_BUFFER_MS = 60 * 1000;

// Prevent malformed or short-lived sigs from creating an immediate refresh loop.
export const SAS_MIN_REFRESH_DELAY_MS = 5 * 1000;

// How long (ms) to wait before retrying after a failed sig fetch.
export const SAS_RETRY_INTERVAL_MS = 30 * 1000;

export const MAX_CONSECUTIVE_AUTH_FAILURES = 3;
export const AUTH_FAILURE_WINDOW_MS = 60 * 1000;

export const AIRPORT_VIEWS = [
  { label: "Runway View", zoom: 13 },
  { label: "10 miles", zoom: 12 },
  { label: "50 miles", zoom: 10 },
];

export interface IMapSas {
  expires: string;
  sig: string;
}

export const BASE_MAP = {
  tileSetID: "microsoft.base.road",
  errorMessage: "Base map failed to load.",
  retryMessage: "Please try again later.",
};

export const WEATHER_MAP = {
  tileSetID: "microsoft.weather.radar.main",
  maxNativeZoom: 15,
  errorMessage: "Weather map failed to load.",
  retryMessage: "Retrying in 5 min.",
};

export const MAP_ERROR_CONFIG = {
  BASE: {
    errorMessage: "Base map failed to load.",
    retryMessage: "Please try again later.",
  },
  WEATHER: {
    errorMessage: "Weather map failed to load.",
    retryMessage: "Retrying in 5 min.",
  },
  RESOURCES: {
    errorMessage: "Map resources failed to load.",
    retryMessage: "",
  },
  SAS: {
    errorMessage: "Map authorization failed to load.",
    retryMessage: "Retrying...",
  },
  NO_FLIGHTS: {
    errorMessage: "No flights found.",
    retryMessage: "",
  },
} as const;

export type MapErrorId = keyof typeof MAP_ERROR_CONFIG;

export const DEFAULT_MAP_ERROR = {
  errorMessage: "An unexpected map error occurred.",
  retryMessage: "",
};

export interface StandDisplayStatus {
  label: string;
  type: BadgeStatusType;
  color: string;
}

export interface IRunwayCondition {
  id: string;
  value: string;
  description: string;
  status?: any;
}

export interface IRunwayInfo {
  validFrom: string;
  validTo: string;
  segments: IRunwayCondition[];
}

export interface IRSCInfo {
  current: IRunwayInfo;
  prior: IRunwayInfo;
}

export type MAP_RESOURCE_STATUS = "OPERATIONAL" | "NOT OPERATIONAL" | "UNKNOWN";
export type MAP_RESOURCE_OCCUPANCY_STATUS =
  | "OCCUPIED"
  | "UNOCCUPIED"
  | "UNKNOWN";
